/* SpecBadgePreview.jsx — Build Badge attached to ONE Inner Specialization.
 *
 * Loads via /api/specs/{spec_id}/badge — strictly gated:
 *   • 423 if the spec is not in the user's kept set (or not in committed branch)
 *   • 423 if a different spec's slug is hit by direct URL
 *
 * No "list of badges" anywhere on this page. No browser back to a catalogue.
 * Back goes only to the parent specialization preview. Reset is the same
 * one-time reset of the Inner Specializations funnel — there is no second
 * reset here.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Trophy, ShieldCheck, Github, ExternalLink, Video, AlertTriangle,
  ArrowLeft, Sparkles, Clock, Lock, Award, Loader2,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SpecBadgePreview() {
  const { program, id } = useParams();         // /specializations/preview/btech/1/badge
  const specId = `${program}_${id}`;
  const { user } = useAuth();
  const navigate = useNavigate();

  const [brief, setBrief] = useState(null);
  const [earned, setEarned] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [lockMsg, setLockMsg] = useState('');
  const [form, setForm] = useState({ github_url: '', deployed_url: '', video_url: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user === null) return;
    if (user === false) { navigate(`/signin?next=/specializations/preview/${program}/${id}/badge`, { replace: true }); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/specs/${specId}/badge`);
        setBrief(r.data.brief);
        setEarned(r.data.earned);
        if (r.data.earned) {
          setForm({
            github_url:   r.data.earned.github_url   || '',
            deployed_url: r.data.earned.deployed_url || '',
            video_url:    r.data.earned.video_url    || '',
            notes:        r.data.earned.notes        || '',
          });
        }
      } catch (e) {
        const code = e?.response?.status;
        const d = e?.response?.data?.detail;
        if (code === 423 || code === 403 || code === 404) {
          setLocked(true);
          setLockMsg(typeof d === 'object' ? (d.message || 'Locked.') : (d || 'Locked.'));
        } else {
          setError(typeof d === 'string' ? d : 'Could not load.');
        }
      } finally { setLoading(false); }
    })();
  }, [user, specId, program, id, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!(form.github_url || '').match(/^https?:\/\/(github\.com|gitlab\.com|bitbucket\.org)\//i)) {
      setError('Please paste a valid GitHub / GitLab / Bitbucket URL.');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/specs/${specId}/badge/submit`, {
        github_url:   form.github_url.trim(),
        deployed_url: form.deployed_url || undefined,
        video_url:    form.video_url    || undefined,
        notes:        form.notes        || undefined,
      });
      setEarned(r.data.badge);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (d?.message || err?.message || 'Submission failed.'));
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF9F1]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (locked) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <main className="pt-28 pb-16 px-4">
          <div className="max-w-xl mx-auto text-center bg-white border-2 border-amber-200 rounded-3xl p-10 shadow-md" data-testid="spec-badge-locked">
            <Lock size={32} className="mx-auto text-gray-400 mb-3" />
            <h1 className="text-2xl font-extrabold text-gray-900">Locked</h1>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">{lockMsg || 'This badge is not in your set.'}</p>
            <Link to="/specializations"
              className="inline-flex items-center justify-center gap-2 mt-6 bg-teal-600 hover:bg-teal-700 text-white font-extrabold px-6 py-3 rounded-full">
              Back to my picks
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (!brief) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]"><Navbar /><div className="pt-32 text-center text-rose-700 font-semibold">{error || 'Not found.'}</div></div>
    );
  }

  const inp = 'mt-1 w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white text-[15px]';

  return (
    <div className="min-h-screen bg-[#FFF9F1]">
      <Navbar />
      <main className="pt-24 md:pt-28 pb-16 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <Link to={`/specializations/preview/${program}/${id}`}
            className="inline-flex items-center gap-1 text-sm font-semibold text-gray-600 hover:text-gray-900 mb-5"
            data-testid="spec-badge-back-btn">
            <ArrowLeft size={15} /> Back to this preview
          </Link>

          <section className="bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 text-white rounded-3xl px-6 py-8 md:px-10 md:py-10 mb-7 shadow-xl">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold tracking-widest uppercase bg-white/15 backdrop-blur px-3 py-1 rounded-full mb-3">
              <Trophy size={11} /> {brief.department}
            </span>
            <h1 className="text-2xl md:text-4xl font-black tracking-tight leading-tight" data-testid="spec-badge-title">
              Build Badge: {brief.name}
            </h1>
            <p className="text-base md:text-lg text-white/90 mt-2 max-w-2xl">
              Tied to this specialization. Ship the project below, paste your repo, and earn your Bronze badge.
            </p>
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              {brief.time_hint && <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full font-semibold"><Clock size={11}/> {brief.time_hint}</span>}
              <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full font-semibold">🆓 Free · Bronze tier</span>
              <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full font-semibold"><Sparkles size={11}/> AI-reviewed</span>
            </div>
          </section>

          <div className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 space-y-4">
              {[
                { tag: 'STARTER',  body: brief.starter,  hint: 'Quickest path to a Bronze badge.' },
                { tag: 'STRONGER', body: brief.stronger, hint: 'Aim for this if you have a free weekend.' },
                { tag: 'STANDOUT', body: brief.standout, hint: 'Pick this if you want a recruiter to stop and read.' },
              ].filter((b) => b.body).map((b) => (
                <div key={b.tag} className="bg-white rounded-2xl border-2 border-amber-100 p-5" data-testid={`spec-badge-section-${b.tag.toLowerCase()}`}>
                  <div className="text-[10px] font-extrabold tracking-widest uppercase text-amber-700 bg-amber-50 inline-block px-2 py-0.5 rounded-full mb-2">{b.tag}</div>
                  <p className="text-[15px] text-gray-800 leading-relaxed">{b.body}</p>
                  <p className="text-[12px] text-gray-500 italic mt-2">{b.hint}</p>
                </div>
              ))}
              {brief.signal && (
                <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-5">
                  <div className="text-[10px] font-extrabold tracking-widest uppercase text-emerald-700 mb-2">What we look for</div>
                  <p className="text-[14px] text-gray-800 leading-relaxed">{brief.signal}</p>
                </div>
              )}
              {brief.ai_integration && (
                <div className="bg-indigo-50 border-2 border-indigo-200 rounded-2xl p-5">
                  <div className="text-[10px] font-extrabold tracking-widest uppercase text-indigo-700 mb-2">How AI shows up in this role</div>
                  <p className="text-[13px] text-gray-800 leading-relaxed">{brief.ai_integration}</p>
                </div>
              )}
            </div>

            <aside className="lg:col-span-2">
              {earned?.github_url ? (
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-3xl border-2 border-emerald-300 shadow-md p-6 sticky top-24" data-testid="spec-badge-earned">
                  <Award size={28} className="text-emerald-700 mb-2" />
                  <div className="text-[10px] font-extrabold tracking-widest uppercase text-emerald-800">Badge earned</div>
                  <h2 className="text-lg font-black text-emerald-900 mb-3">{brief.name}</h2>
                  <a href={earned.github_url} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-800 hover:underline break-all">
                    <Github size={13}/> {earned.github_url} <ExternalLink size={11}/>
                  </a>
                  {earned.earned_at && <div className="text-[11px] text-emerald-700 mt-3">Earned · {new Date(earned.earned_at).toLocaleDateString()}</div>}
                </div>
              ) : (
                <div className="bg-white rounded-3xl border-2 border-amber-300 shadow-md p-6 sticky top-24" data-testid="spec-badge-submit">
                  <div className="text-[10px] font-extrabold tracking-widest uppercase text-amber-700 mb-1">Submit your build</div>
                  <h2 className="text-lg font-black text-gray-900 mb-3">Earn this badge</h2>
                  {error && (
                    <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-xl p-3 mb-4 text-sm text-rose-800">
                      <AlertTriangle size={15} className="text-rose-600 mt-0.5 shrink-0"/> {error}
                    </div>
                  )}
                  <form onSubmit={submit} className="space-y-3">
                    <label className="block">
                      <span className="text-xs font-bold tracking-wider uppercase text-gray-600 flex items-center gap-1"><Github size={11}/> GitHub repo URL <span className="text-rose-600">*</span></span>
                      <input required value={form.github_url} onChange={(e) => setForm({ ...form, github_url: e.target.value })} placeholder="https://github.com/you/project" className={inp}/>
                    </label>
                    <label className="block">
                      <span className="text-xs font-bold tracking-wider uppercase text-gray-600 flex items-center gap-1"><ExternalLink size={11}/> Live deployed URL</span>
                      <input value={form.deployed_url} onChange={(e) => setForm({ ...form, deployed_url: e.target.value })} placeholder="https://your-project.vercel.app" className={inp}/>
                    </label>
                    <label className="block">
                      <span className="text-xs font-bold tracking-wider uppercase text-gray-600 flex items-center gap-1"><Video size={11}/> 5-min walkthrough video URL</span>
                      <input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://loom.com/share/xxx or YouTube link" className={inp}/>
                    </label>
                    <label className="block">
                      <span className="text-xs font-bold tracking-wider uppercase text-gray-600">Decisions doc (paste, 200+ chars) <span className="text-rose-600">*</span></span>
                      <textarea required rows={6} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Why this approach? Where did AI help? What did you correct? What stayed your judgment?" className={inp} maxLength={4000}/>
                      <span className="block text-[11px] text-gray-500 mt-1">{form.notes.length}/4000 · 200+ chars recommended</span>
                    </label>
                    <button type="submit" disabled={busy}
                      className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-gray-900 font-extrabold px-5 py-3 rounded-full transition-colors">
                      {busy ? 'Reviewing your build…' : 'Submit for review →'}
                    </button>
                    <p className="text-[11px] text-gray-500 text-center flex items-center justify-center gap-1">
                      <ShieldCheck size={12} className="text-emerald-600"/> Free · AI-reviewed · No payment for Bronze
                    </p>
                  </form>
                </div>
              )}
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
